import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import CTABanner from "../components/CTABanner";
import { Reveal } from "../components/motion";
import { SERVICES, PROCESS } from "../lib/site";

const ServicePage = () => {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/" replace />;

  const idx = SERVICES.indexOf(service);
  const prev = SERVICES[(idx + SERVICES.length - 1) % SERVICES.length];
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <>
      <SEO
        title={`${service.name} | Esskay Homes`}
        description={service.short}
        path={`/services/${service.slug}`}
      />

      <PageHero
        eyebrow={`Esskay Homes · ${service.name}`}
        title={service.name}
        sub={service.tagline}
        image={service.image}
        crumbs={["Services", service.name]}
      />

      {/* INTRO + WHAT'S INCLUDED */}
      <section className="container-ek grid gap-16 py-24 lg:grid-cols-[1.5fr_1fr] lg:gap-24 lg:py-32" data-testid="service-intro">
        <div>
          <Reveal>
            <p className="eyebrow flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-gold" />
              The Service
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl">
              {service.tagline}
            </h2>
          </Reveal>
          {service.description.map((p, i) => (
            <Reveal key={i} delay={0.18 + i * 0.08}>
              <p className="mt-6 text-base leading-relaxed text-mist md:text-lg">{p}</p>
            </Reveal>
          ))}

          <Reveal delay={0.3}>
            <div className="relative mt-12 overflow-hidden">
              <img
                src={service.altImage}
                alt={`${service.name} detail`}
                loading="lazy"
                className="aspect-[16/9] w-full object-cover"
              />
              <div className="absolute inset-0 border border-white/10" />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <aside
            data-testid="service-included"
            className="h-fit border border-white/10 bg-navy2/70 p-8 lg:sticky lg:top-32"
          >
            <p className="eyebrow">What's Included</p>
            <h3 className="mt-4 font-serif text-2xl font-medium text-white md:text-3xl">
              Everything, handled.
            </h3>
            <ul className="mt-8 space-y-4">
              {service.includes.map((item, i) => (
                <li key={i} className="flex items-start gap-3.5 text-sm leading-relaxed text-white/85">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border border-gold/50">
                    <Check className="h-3 w-3 text-gold" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              data-testid="service-included-cta"
              className="btn-gold group mt-10 w-full"
            >
              Get a Free Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </aside>
        </Reveal>
      </section>

      {/* PROCESS */}
      <section className="bg-navy2/40 py-24 lg:py-32" data-testid="service-process">
        <div className="container-ek">
          <div className="mb-14">
            <Reveal>
              <p className="eyebrow flex items-center gap-3">
                <span className="inline-block h-px w-10 bg-gold" />
                The Process
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl">
                Survey. Design. Build.{" "}
                <em className="italic text-gold">Handover.</em>
              </h2>
            </Reveal>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.num} delay={0.08 * i} className="h-full">
                <div
                  data-testid={`service-process-step-${i}`}
                  className="group h-full border border-white/10 bg-navy3/50 p-8 transition-all duration-500 hover:border-gold/40"
                >
                  <span className="font-serif text-4xl font-medium text-gold/50 transition-colors duration-500 group-hover:text-gold">
                    {p.num}
                  </span>
                  <h3 className="mt-6 font-serif text-xl font-medium text-white md:text-2xl">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-mist">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OTHER SERVICES */}
      <section className="container-ek py-20 lg:py-24" data-testid="service-others">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-8 border border-white/10 p-8 md:flex-row md:items-center md:p-10">
            <Link
              to={prev.slug === next.slug ? `/services/${prev.slug}` : `/services/${prev.slug}`}
              data-testid="service-prev-link"
              className="group flex items-center gap-4 text-left"
            >
              <ArrowLeft className="h-4 w-4 text-gold transition-transform duration-300 group-hover:-translate-x-1" />
              <span>
                <span className="block text-[10px] uppercase tracking-[0.25em] text-mist">
                  Previous Service
                </span>
                <span className="mt-1 block font-serif text-xl text-white">{prev.name}</span>
              </span>
            </Link>
            <Link
              to={next.slug}
              data-testid="service-next-link"
              className="group flex items-center gap-4 text-right"
            >
              <span>
                <span className="block text-[10px] uppercase tracking-[0.25em] text-mist">
                  Next Service
                </span>
                <span className="mt-1 block font-serif text-xl text-white">{next.name}</span>
              </span>
              <ArrowRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>

      <CTABanner
        title={["Ready to plan", "your project?"]}
        sub={`Tell us about your ${service.name.toLowerCase()} — we'll arrange a free survey and send a detailed, itemised quote.`}
      />
    </>
  );
};

export default ServicePage;
